import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export interface DirectEscalationData {
  escalationId: string;
  intercomConversationId: string;
  noticeTopic: string;
  triageThreadId: string;
}

export interface OpenPanelOptions {
  prefillInput?: string;
  autoSubmit?: boolean;
  directEscalation?: DirectEscalationData;
}

interface AiSlidePanelContextValue {
  isOpen: boolean;
  panelOptions: OpenPanelOptions | null;
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  open: (options?: OpenPanelOptions) => void;
  close: () => void;
  toggle: (options?: OpenPanelOptions) => void;
  clearPanelOptions: () => void;
}

const AiSlidePanelContext = createContext<AiSlidePanelContextValue | undefined>(
  undefined,
);

export const AiSlidePanelProvider = memo(function AiSlidePanelProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelOptions, setPanelOptions] = useState<OpenPanelOptions | null>(
    null,
  );
  const [panelWidth, setPanelWidth] = useState(364);

  const open = useCallback((options?: OpenPanelOptions) => {
    if (options) {
      setPanelOptions(options);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setPanelOptions(null);
  }, []);

  const toggle = useCallback((options?: OpenPanelOptions) => {
    setIsOpen((prev) => {
      if (!prev && options) {
        setPanelOptions(options);
      }
      if (prev) {
        setPanelOptions(null);
      }
      return !prev;
    });
  }, []);

  const clearPanelOptions = useCallback(() => {
    setPanelOptions(null);
  }, []);

  return (
    <AiSlidePanelContext.Provider
      value={{
        isOpen,
        panelOptions,
        panelWidth,
        setPanelWidth,
        open,
        close,
        toggle,
        clearPanelOptions,
      }}
    >
      {children}
    </AiSlidePanelContext.Provider>
  );
});

export function useAiSlidePanel(): AiSlidePanelContextValue {
  const context = useContext(AiSlidePanelContext);
  if (!context) {
    throw new Error(
      "useAiSlidePanel must be used within an AiSlidePanelProvider",
    );
  }
  return context;
}
